import { ReactNode } from 'react';

export interface TicketLine {
  key: string;
  label: string;
  detail?: string;
  amount?: string;
}

interface OrderTicketProps {
  title?: string;
  meta?: { label: string; value: string }[];
  // Priced lines, only on the customer's own offer; the request form shows meta only
  lines?: TicketLine[];
  total?: { label: string; value: string };
  note?: ReactNode;
  // Rubber stamp across the ticket, e.g. "Aangevraagd" after sending
  stamp?: { text: string; tone: 'red' | 'brown' | 'green' };
  className?: string;
}

const STAMP_TONES = {
  red: 'border-bl-red text-bl-red',
  brown: 'border-bl-brown text-bl-brown',
  green: 'border-emerald-700 text-emerald-700',
};

// The order ticket from the burger counter: it fills in while the customer fills in the
// form, and gets stamped once the request is sent, or the offer is signed or confirmed.
export function OrderTicket({ title = 'Jouw bon', meta = [], lines = [], total, note, stamp, className = '' }: OrderTicketProps) {
  return (
    <div className={`ticket px-6 pb-7 pt-6 ${className}`} aria-label={title}>
      <div className="text-center">
        <img src="/images/logo.png" alt="" className="mx-auto h-9 w-auto opacity-90" />
        <p className="mt-2 font-display text-xs font-bold uppercase tracking-[0.3em] text-bl-brown">{title}</p>
      </div>

      <div className="ticket-rule my-4" />

      {meta.length > 0 ? (
        <dl className="space-y-2.5 text-sm" aria-live="polite">
          {meta.map((item) => (
            <div key={item.label} className="ticket-line-in flex justify-between gap-4">
              <dt className="text-bl-black/55">{item.label}</dt>
              <dd className="text-right font-medium">{item.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-center text-sm text-bl-black/45">Je keuzes verschijnen hier.</p>
      )}

      {lines.length > 0 && (
        <>
          <div className="ticket-rule my-4" />
          <ul className="space-y-3 text-sm">
            {lines.map((line) => (
              <li key={line.key} className="flex justify-between gap-4">
                <div>
                  <p className="font-medium">{line.label}</p>
                  {line.detail && <p className="text-xs text-bl-black/55">{line.detail}</p>}
                </div>
                {line.amount && <p className="whitespace-nowrap font-medium tabular-nums">{line.amount}</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      {total && (
        <>
          <div className="ticket-rule my-4" />
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-bold uppercase tracking-wider">{total.label}</p>
            <p className="font-display text-3xl font-black tabular-nums">{total.value}</p>
          </div>
        </>
      )}

      {note && (
        <>
          <div className="ticket-rule my-4" />
          <div className="text-xs leading-relaxed text-bl-black/55">{note}</div>
        </>
      )}

      {stamp && (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden" aria-hidden="true">
          <span className={`stamp stamp-in bg-bl-cream/80 ${STAMP_TONES[stamp.tone]}`}>{stamp.text}</span>
        </div>
      )}
    </div>
  );
}
